/**
 * SIGNALS-PRACTICE-LENS-3W curator practice assignment — the pure contract.
 *
 * A curator may include a signal in a practice (none -> included) or clear that
 * inclusion (included -> none, with a reason). Both decisions go through the
 * practice curation Edge Function (named only in practice-assignment-api.ts),
 * which re-derives the capability, and the audited SECURITY DEFINER writer,
 * which enforces the transition table and the per-cell optimistic version. Nothing in this module talks to the network or to
 * React: it holds the wire parsing, the transition and reason rules, the intent
 * identity, the failure policy and the session state machine, so every one of
 * them is exercised directly in Node Vitest.
 *
 * Three rules are load-bearing throughout:
 *
 *  - A cell version is PostgreSQL bigint TEXT. It is validated as canonical
 *    decimal text and passed back verbatim; it never passes through Number, so a
 *    version past 2^53 is sent exactly as it was read.
 *  - The only state the UI shows is the state get_signal_practices returned. A
 *    2xx write - including an idempotent replay, which reports the RESULT OF THE
 *    REPLAYED REQUEST rather than the current projection - is followed by an
 *    authoritative re-read, never trusted as current state.
 *  - One key controller owns all eight rows, so only one decision is ever in
 *    flight, and key identity follows the accepted intent-key lifecycle.
 */

import {
  canonicalJson,
  createIntentKeyController,
  normalizeReason,
  type IntentKeyController,
  type SubmissionBlock,
} from "@/features/curator-queue/decision-intent";
import {
  PRACTICE_KEYS,
  isPracticeKey,
  type PracticeClassification,
  type PracticeKey,
} from "./practice-lens-ui";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const PRACTICE_ASSIGNMENT_STATES = ["none", "included"] as const;
export type PracticeAssignmentState = (typeof PRACTICE_ASSIGNMENT_STATES)[number];

export const PRACTICE_ASSIGNMENT_KINDS = ["include", "clear"] as const;
export type PracticeAssignmentKind = (typeof PRACTICE_ASSIGNMENT_KINDS)[number];

/** The accepted transition table, restated from the server contract. */
export const PRACTICE_ASSIGNMENT_TRANSITIONS: Readonly<
  Record<PracticeAssignmentKind, { from: PracticeAssignmentState; to: PracticeAssignmentState }>
> = {
  include: { from: "none", to: "included" },
  clear: { from: "included", to: "none" },
};

/** The server's MAX_ASSIGNMENT_REASON_LENGTH, measured on the trimmed reason. */
export const MAX_PRACTICE_REASON_LENGTH = 2000;

const BIGINT_MAX = 9223372036854775807n;
const CANONICAL_DECIMAL = /^(0|[1-9][0-9]*)$/;

export function isPracticeAssignmentState(value: unknown): value is PracticeAssignmentState {
  return (
    typeof value === "string" && (PRACTICE_ASSIGNMENT_STATES as readonly string[]).includes(value)
  );
}

/**
 * Canonical non-negative decimal text within bigint range: no sign, no leading
 * zero, no whitespace, no exponent, and never a number. A value that has
 * already been decoded as a JavaScript number cannot be made exact again, so it
 * is refused rather than repaired.
 */
export function isCanonicalVersionText(value: unknown): value is string {
  return typeof value === "string" && CANONICAL_DECIMAL.test(value) && BigInt(value) <= BIGINT_MAX;
}

/** expected + 1, computed in bigint and returned as canonical text. */
export function nextVersionText(version: string): string {
  if (!isCanonicalVersionText(version)) {
    throw new Error("cell version must be canonical bigint text");
  }
  return (BigInt(version) + 1n).toString();
}

// ---------------------------------------------------------------------------
// get_signal_practices
// ---------------------------------------------------------------------------

export interface PracticeAssignmentCell {
  practiceKey: PracticeKey;
  /** The server's display_name, rendered verbatim. */
  displayName: string;
  displayOrder: number;
  state: PracticeAssignmentState;
  /** Canonical bigint text, exactly as the server issued it. */
  cellVersion: string;
  currentEventId: string | null;
}

export interface SignalPracticeAssignments {
  signalId: string;
  /** All eight active peers, in the server's display_order. */
  practices: readonly PracticeAssignmentCell[];
}

function asRecord(value: unknown, what: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${what} was not an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Parses get_signal_practices' `data`. Refuses, rather than repairs, anything
 * that is not exactly the eight canonical peers for the requested signal: a
 * different signal id, a missing, extra, unknown or duplicate practice, an
 * out-of-order row, an unknown state, a non-canonical version, or a cell whose
 * version and current event disagree about whether it was ever decided.
 */
export function parseSignalPracticeAssignments(
  raw: unknown,
  signalId: string
): SignalPracticeAssignments {
  const root = asRecord(raw, "Signal practice assignments");
  if (root.signal_id !== signalId) {
    throw new Error("Signal practice assignments named another signal");
  }
  if (!Array.isArray(root.practices) || !Array.isArray(root.events)) {
    throw new Error("Signal practice assignments are missing practices or events");
  }
  if (root.practices.length !== PRACTICE_KEYS.length) {
    throw new Error("Signal practice assignments did not list every practice exactly once");
  }
  const seen = new Set<string>();
  let previousOrder = 0;
  const practices: PracticeAssignmentCell[] = [];
  for (const row of root.practices) {
    const r = asRecord(row, "Signal practice assignment row");
    if (!isPracticeKey(r.practice_key)) {
      throw new Error("Signal practice assignment row has an unknown practice_key");
    }
    if (seen.has(r.practice_key)) {
      throw new Error("Signal practice assignments returned a duplicate practice");
    }
    if (typeof r.display_name !== "string" || r.display_name.trim().length === 0) {
      throw new Error("Signal practice assignment row has no display_name");
    }
    if (
      typeof r.display_order !== "number" ||
      !Number.isSafeInteger(r.display_order) ||
      r.display_order <= previousOrder
    ) {
      throw new Error("Signal practice assignments are not in display order");
    }
    if (!isPracticeAssignmentState(r.state)) {
      throw new Error("Signal practice assignment row has an unknown state");
    }
    if (!isCanonicalVersionText(r.cell_version)) {
      throw new Error("Signal practice assignment row has a non-canonical cell_version");
    }
    const neverDecided = r.cell_version === "0";
    if (neverDecided) {
      if (r.state !== "none" || r.current_event_id !== null) {
        throw new Error("An undecided practice cell carries a state or event");
      }
    } else if (typeof r.current_event_id !== "string" || r.current_event_id.length === 0) {
      throw new Error("A decided practice cell has no current event");
    }
    seen.add(r.practice_key);
    previousOrder = r.display_order;
    practices.push({
      practiceKey: r.practice_key,
      displayName: r.display_name,
      displayOrder: r.display_order,
      state: r.state,
      cellVersion: r.cell_version,
      currentEventId: neverDecided ? null : (r.current_event_id as string),
    });
  }
  return { signalId, practices };
}

// ---------------------------------------------------------------------------
// Transitions and reasons
// ---------------------------------------------------------------------------

/** The single decision legal from a state. */
export function legalKindFor(state: PracticeAssignmentState): PracticeAssignmentKind {
  return state === "none" ? "include" : "clear";
}

export type PracticeTransitionVerdict = "legal" | "self" | "invalid";

/** Mirrors the server: a self transition is decided first, then legality. */
export function classifyPracticeTransition(
  kind: PracticeAssignmentKind,
  current: PracticeAssignmentState
): PracticeTransitionVerdict {
  const transition = PRACTICE_ASSIGNMENT_TRANSITIONS[kind];
  if (transition.to === current) return "self";
  return transition.from === current ? "legal" : "invalid";
}

export type ClearReasonProblem = "required" | "too_long";

/**
 * Trim, treat empty as absent, then judge - exactly the server's order, so
 * whitespace never passes here and is then refused there.
 */
export function validateClearReason(raw: string | null | undefined): {
  reason: string | null;
  problem: ClearReasonProblem | null;
} {
  const reason = normalizeReason(raw);
  if (reason === null) return { reason, problem: "required" };
  if (reason.length > MAX_PRACTICE_REASON_LENGTH) return { reason, problem: "too_long" };
  return { reason, problem: null };
}

// ---------------------------------------------------------------------------
// Intent identity and the request body
// ---------------------------------------------------------------------------

export interface PracticeAssignmentIntent {
  signalId: string;
  practiceKey: PracticeKey;
  kind: PracticeAssignmentKind;
  /** Verbatim from the cell read this decision is made against. */
  expectedState: PracticeAssignmentState;
  /** Verbatim from the cell read this decision is made against. */
  expectedCellVersion: string;
  /** Always null for include; the trimmed, bounded reason for clear. */
  reason: string | null;
}

export type PracticeIntentRefusal = "illegal_transition" | "reason_required" | "reason_too_long";

export type PracticeIntentBuild =
  | { ok: true; intent: PracticeAssignmentIntent }
  | { ok: false; refusal: PracticeIntentRefusal };

/**
 * Builds a decision against one exact cell read. Expected state and version are
 * copied from that read and from nowhere else.
 */
export function buildPracticeAssignmentIntent(
  signalId: string,
  cell: PracticeAssignmentCell,
  kind: PracticeAssignmentKind,
  rawReason?: string | null
): PracticeIntentBuild {
  if (classifyPracticeTransition(kind, cell.state) !== "legal") {
    return { ok: false, refusal: "illegal_transition" };
  }
  let reason: string | null = null;
  if (kind === "clear") {
    const verdict = validateClearReason(rawReason);
    if (verdict.problem === "required") return { ok: false, refusal: "reason_required" };
    if (verdict.problem === "too_long") return { ok: false, refusal: "reason_too_long" };
    reason = verdict.reason;
  }
  return {
    ok: true,
    intent: {
      signalId,
      practiceKey: cell.practiceKey,
      kind,
      expectedState: cell.state,
      expectedCellVersion: cell.cellVersion,
      reason,
    },
  };
}

/**
 * The complete identity of one practice decision: every field the server
 * fingerprints (the actor is the session itself, and no metadata is ever sent).
 * Expected state and version are identity, not context - acting against newer
 * truth is a different decision.
 */
export function practiceIntentFingerprint(intent: PracticeAssignmentIntent): string {
  return canonicalJson({
    contract: "practice_assignment_intent",
    signal_id: intent.signalId,
    practice_key: intent.practiceKey,
    event_kind: intent.kind,
    expected_state: intent.expectedState,
    expected_cell_version: intent.expectedCellVersion,
    reason: intent.reason,
    metadata: {},
  });
}

/**
 * The exact practice curation request body. Include carries no reason key at all;
 * clear always carries its reason. No evidence field, no metadata, no actor.
 */
export function buildPracticeAssignmentBody(
  intent: PracticeAssignmentIntent,
  eventKey: string
): Record<string, unknown> {
  if (!isCanonicalVersionText(intent.expectedCellVersion)) {
    throw new Error("expected_cell_version must be canonical bigint text");
  }
  const body: Record<string, unknown> = {
    operation: intent.kind,
    signal_id: intent.signalId,
    practice_key: intent.practiceKey,
    event_key: eventKey,
    expected_state: intent.expectedState,
    expected_cell_version: intent.expectedCellVersion,
  };
  if (intent.kind === "clear") {
    if (intent.reason === null) throw new Error("clear requires a reason");
    body.reason = intent.reason;
  }
  return body;
}

// ---------------------------------------------------------------------------
// The write result
// ---------------------------------------------------------------------------

/**
 * Checks a 2xx write body against the request that produced it. The result is
 * used for ONE thing - telling a new event from a replay - and is never used as
 * current projection state. Anything that does not exactly continue the
 * expected version for this signal, practice, kind and key is refused.
 */
export function parsePracticeAssignmentResult(
  raw: unknown,
  intent: PracticeAssignmentIntent,
  eventKey: string
): { replay: boolean } {
  const root = asRecord(raw, "Practice assignment result");
  if (typeof root.idempotent !== "boolean") {
    throw new Error("Practice assignment result has no idempotent flag");
  }
  const event = asRecord(root.event, "Practice assignment event");
  const assignment = asRecord(root.assignment, "Practice assignment");
  const transition = PRACTICE_ASSIGNMENT_TRANSITIONS[intent.kind];
  const resulting = nextVersionText(intent.expectedCellVersion);
  if (
    event.event_key !== eventKey ||
    event.signal_id !== intent.signalId ||
    event.practice_key !== intent.practiceKey ||
    event.event_kind !== intent.kind ||
    event.prior_state !== intent.expectedState ||
    event.resulting_state !== transition.to
  ) {
    throw new Error("Practice assignment result does not describe this decision");
  }
  if (
    event.prior_cell_version !== intent.expectedCellVersion ||
    event.resulting_cell_version !== resulting ||
    assignment.cell_version !== resulting
  ) {
    throw new Error("Practice assignment result does not continue the expected version");
  }
  if (
    assignment.signal_id !== intent.signalId ||
    assignment.practice_key !== intent.practiceKey ||
    assignment.state !== transition.to
  ) {
    throw new Error("Practice assignment result carries another cell");
  }
  return { replay: root.idempotent };
}

export type PracticeWriteOutcome =
  | { status: "recorded"; replay: boolean }
  /** A 2xx whose body could not be read. The server accepted the request. */
  | { status: "unreadable" };

// ---------------------------------------------------------------------------
// Failure policy
// ---------------------------------------------------------------------------

export type PracticeFailureClass = "conflict" | "retryable" | "non_retryable";

/**
 * The practice failure matrix, over the classified error shape the curator
 * error module produces ({ category, httpStatus }).
 *
 *   409                          conflict: re-read before anything else is sent
 *   5xx, network, relay, timeout retryable with the SAME key: the outcome is
 *                                uncertain and the server replays the key
 *   400/401/403/404, defensive   non-retryable for this fingerprint only: the
 *   422, any other status, and   server would repeat the same answer, and an
 *   anything unclassifiable      unclassifiable error may be a client defect
 */
export function classifyPracticeFailure(error: unknown): PracticeFailureClass {
  if (error === null || typeof error !== "object") return "non_retryable";
  const { category, httpStatus } = error as { category?: unknown; httpStatus?: unknown };
  if (category === "network" || category === "relay") return "retryable";
  if (category === "server_http" && typeof httpStatus === "number") {
    if (httpStatus === 409) return "conflict";
    if (httpStatus >= 500 && httpStatus <= 599) return "retryable";
  }
  return "non_retryable";
}

/** Applies the matrix to the controller and returns the resulting block. */
export function applyPracticeFailure(
  controller: IntentKeyController<PracticeAssignmentIntent>,
  failure: PracticeFailureClass
): SubmissionBlock | null {
  if (failure === "conflict") controller.markConflict();
  else if (failure === "non_retryable") controller.markNonRetryable();
  return controller.blockReason();
}

// ---------------------------------------------------------------------------
// Refresh boundary and copy
// ---------------------------------------------------------------------------

/**
 * Exactly what an accepted decision can have changed for this viewer: this
 * signal's detail (its practice classification) and the feed (a practice lens
 * may gain or lose the signal). Invalidated, never reset or removed, so loaded
 * pages and the cursor walk survive. Tab counts, triage, the practice registry,
 * history, assignable users and every curator query are deliberately absent.
 */
export function practiceAssignmentRefreshKeys(signalId: string): readonly (readonly string[])[] {
  return [
    ["signals", "detail", signalId],
    ["signals", "feed"],
  ];
}

/**
 * Practices whose effective membership the detail read attributes to either
 * governed source-event routing or governed legacy-signal routing. Both are
 * machine bases that curator Include/Clear actions do not remove.
 */
export function automatedPracticeKeys(
  classification: PracticeClassification | null | undefined
): ReadonlySet<PracticeKey> {
  const keys = new Set<PracticeKey>();
  if (!classification) return keys;
  for (const practice of classification.practices) {
    if (practice.bases.includes("automated") || practice.bases.includes("legacy_automated")) {
      keys.add(practice.practiceKey);
    }
  }
  return keys;
}

export const PRACTICE_KIND_LABELS: Readonly<Record<PracticeAssignmentKind, string>> = {
  include: "Include",
  clear: "Clear",
};

export function practiceRowCopy(
  cell: PracticeAssignmentCell,
  automated: boolean
): { stateLabel: string; actionLabel: string; automatedNote: string | null } {
  return {
    stateLabel: cell.state === "included" ? "Included by a curator" : "Not included by a curator",
    actionLabel: PRACTICE_KIND_LABELS[legalKindFor(cell.state)],
    automatedNote: automated
      ? "Automated routing also places this signal here. Include and Clear change only the curator inclusion."
      : null,
  };
}

function outcomeNotice(
  outcome: PracticeWriteOutcome,
  kind: PracticeAssignmentKind,
  displayName: string
): string {
  if (outcome.status === "unreadable") {
    return "The server accepted the request, but its response could not be read. The current assignments are being re-read.";
  }
  if (outcome.replay) {
    return `That decision for ${displayName} was already recorded.`;
  }
  return kind === "include" ? `Included in ${displayName}.` : `Cleared from ${displayName}.`;
}

function failureMessage(error: unknown): string {
  return error instanceof Error && error.message.length > 0 ? error.message : "The request failed.";
}

// ---------------------------------------------------------------------------
// The session state machine
// ---------------------------------------------------------------------------

export type PracticeReadStatus = "idle" | "reading" | "ready" | "error";

export interface PracticeAttemptFailure {
  practiceKey: PracticeKey;
  kind: PracticeAssignmentKind;
  /** The normalized reason the failed attempt carried. */
  reason: string | null;
  failure: PracticeFailureClass;
  message: string;
}

export interface PracticeSessionSnapshot {
  /** Whether the curator has opened "Manage practice assignments". */
  expanded: boolean;
  read: PracticeReadStatus;
  readError: string | null;
  /** Only ever the latest successful get_signal_practices read, or null. */
  cells: readonly PracticeAssignmentCell[] | null;
  /** The row whose confirm step is open, and the decision it was opened for. */
  confirm: { practiceKey: PracticeKey; kind: PracticeAssignmentKind } | null;
  /** The row whose decision is in flight. At most one, for all eight rows. */
  pendingFor: PracticeKey | null;
  failure: PracticeAttemptFailure | null;
  blockReason: SubmissionBlock | null;
  notice: string | null;
  /** Increments on every 2xx write. */
  recordedCount: number;
}

export type PracticeSubmitResult =
  | "started"
  | "busy"
  | "blocked"
  | "refused_identical"
  | "not_ready"
  | PracticeIntentRefusal;

export interface PracticeSessionDeps {
  signalId: string;
  newKey: () => string;
  read: (signalId: string) => Promise<SignalPracticeAssignments>;
  write: (intent: PracticeAssignmentIntent, eventKey: string) => Promise<PracticeWriteOutcome>;
  invalidate: (queryKeys: readonly (readonly string[])[]) => void;
}

export interface PracticeAssignmentSession {
  subscribe(listener: () => void): () => void;
  getSnapshot(): PracticeSessionSnapshot;
  /** Opens the control and performs the first read. Nothing is read before. */
  expand(): void;
  /** Closes and forgets everything. Refused while a decision is in flight. */
  collapse(): boolean;
  /** The authoritative re-read; the only way to clear a block. */
  refresh(): boolean;
  openConfirm(practiceKey: PracticeKey): boolean;
  cancelConfirm(): void;
  submit(
    practiceKey: PracticeKey,
    kind: PracticeAssignmentKind,
    rawReason?: string | null
  ): PracticeSubmitResult;
  attach(): void;
  /** Unmount: forget state and ignore any late response. */
  detach(): void;
  /** Test/diagnostic use. */
  controller(): IntentKeyController<PracticeAssignmentIntent>;
}

const INITIAL_SNAPSHOT: PracticeSessionSnapshot = {
  expanded: false,
  read: "idle",
  readError: null,
  cells: null,
  confirm: null,
  pendingFor: null,
  failure: null,
  blockReason: null,
  notice: null,
  recordedCount: 0,
};

export function createPracticeAssignmentSession(
  deps: PracticeSessionDeps
): PracticeAssignmentSession {
  const controller = createIntentKeyController<PracticeAssignmentIntent>(
    deps.newKey,
    practiceIntentFingerprint
  );
  const listeners = new Set<() => void>();
  let snapshot: PracticeSessionSnapshot = INITIAL_SNAPSHOT;
  let attached = true;
  // Bumped whenever the surface is closed or unmounted; a response that
  // belongs to an older generation is ignored.
  let generation = 0;
  // Bumped per read, so only the latest read may land.
  let readSeq = 0;

  const update = (patch: Partial<PracticeSessionSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    for (const listener of listeners) listener();
  };

  const forget = () => {
    generation += 1;
    readSeq += 1;
    controller.reset();
    update({ ...INITIAL_SNAPSHOT, recordedCount: snapshot.recordedCount });
  };

  const startRead = () => {
    const gen = generation;
    const seq = ++readSeq;
    update({ read: "reading", readError: null });
    deps.read(deps.signalId).then(
      (view) => {
        if (gen !== generation || seq !== readSeq) return;
        // Authoritative state is in hand, so a block - a conflict, or a refusal
        // scoped to one intent - is cleared HERE and nowhere else, and its
        // attempt is discarded so the next decision is freshly keyed.
        const hadBlock = controller.blockReason() !== null;
        if (hadBlock) controller.clearConflict();
        // A confirm step opened for a decision the new read no longer permits
        // closes, rather than silently turning Clear into Include.
        const confirm = snapshot.confirm;
        const confirmCell = confirm
          ? view.practices.find((cell) => cell.practiceKey === confirm.practiceKey)
          : undefined;
        const confirmStillLegal =
          confirm !== null &&
          confirmCell !== undefined &&
          legalKindFor(confirmCell.state) === confirm.kind;
        update({
          read: "ready",
          readError: null,
          cells: view.practices,
          confirm: confirmStillLegal ? confirm : null,
          blockReason: null,
          failure: hadBlock ? null : snapshot.failure,
        });
      },
      (error: unknown) => {
        if (gen !== generation || seq !== readSeq) return;
        // A failed read clears nothing: an outstanding block stands.
        update({ read: "error", readError: failureMessage(error) });
      }
    );
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
    expand() {
      if (!attached || snapshot.expanded) return;
      update({ expanded: true });
      startRead();
    },
    collapse() {
      if (snapshot.pendingFor !== null || controller.isInFlight()) return false;
      forget();
      return true;
    },
    refresh() {
      if (!attached || !snapshot.expanded) return false;
      if (snapshot.pendingFor !== null || snapshot.read === "reading") return false;
      startRead();
      return true;
    },
    openConfirm(practiceKey) {
      if (!attached || snapshot.pendingFor !== null || snapshot.read !== "ready") return false;
      if (snapshot.blockReason === "conflict") return false;
      const cell = snapshot.cells?.find((c) => c.practiceKey === practiceKey);
      if (!cell) return false;
      update({ confirm: { practiceKey, kind: legalKindFor(cell.state) }, notice: null });
      return true;
    },
    cancelConfirm() {
      if (snapshot.pendingFor !== null) return;
      update({ confirm: null });
    },
    submit(practiceKey, kind, rawReason) {
      if (!attached || !snapshot.expanded) return "not_ready";
      if (snapshot.pendingFor !== null || controller.isInFlight()) return "busy";
      if (snapshot.read !== "ready" || snapshot.cells === null) return "not_ready";
      const cell = snapshot.cells.find((c) => c.practiceKey === practiceKey);
      if (!cell) return "not_ready";
      const built = buildPracticeAssignmentIntent(deps.signalId, cell, kind, rawReason);
      if (!built.ok) return built.refusal;
      const intent = built.intent;
      const eventKey = controller.beginAttempt(intent);
      if (eventKey === null) {
        return controller.blockReason() === "conflict" ? "blocked" : "refused_identical";
      }
      const gen = generation;
      update({ pendingFor: practiceKey, failure: null, notice: null });
      deps.write(intent, eventKey).then(
        (outcome) => {
          controller.settleAttempt();
          // The key is spent. The next decision is a new one, against the
          // state the re-read below returns.
          controller.reset();
          // The server state moved whether or not this surface is still open.
          deps.invalidate(practiceAssignmentRefreshKeys(deps.signalId));
          if (gen !== generation) return;
          update({
            pendingFor: null,
            confirm: null,
            failure: null,
            blockReason: null,
            // Pre-write cells are no longer truth, and a replay's result is not
            // the current projection. Nothing is shown until the re-read lands.
            cells: null,
            notice: outcomeNotice(outcome, kind, cell.displayName),
            recordedCount: snapshot.recordedCount + 1,
          });
          startRead();
        },
        (error: unknown) => {
          controller.settleAttempt();
          if (gen !== generation) return;
          const failure = classifyPracticeFailure(error);
          update({
            pendingFor: null,
            blockReason: applyPracticeFailure(controller, failure),
            failure: {
              practiceKey,
              kind,
              reason: intent.reason,
              failure,
              message: failureMessage(error),
            },
          });
        }
      );
      return "started";
    },
    attach() {
      attached = true;
    },
    detach() {
      attached = false;
      forget();
    },
    controller() {
      return controller;
    },
  };
}
